const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
const smoothingAlpha = (frameSeconds, durationMs) => durationMs <= 0 ? 1 : 1 - Math.exp(-frameSeconds / (durationMs / 1000));
const INERT_FRAME = { x: 0, y: 0, velocityX: 0, velocityY: 0, strength: 0 };
/**
 * Fine-pointer input lives outside React state. Canvas bounds are cached until
 * the renderer reports a resize or layout transition that can move the canvas.
 */
export function createPointerField(canvas, config, options = {}) {
    if (options.enabled === false || typeof window === "undefined") {
        return {
            update: () => INERT_FRAME,
            setConfig: () => { },
            setSceneVisible: () => { },
            invalidateBounds: () => { },
            destroy: () => { },
        };
    }
    const finePointerQuery = window.matchMedia("(hover: hover) and (pointer: fine) and (min-width: 901px)");
    const reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    let clientX = 0;
    let clientY = 0;
    let hasMouse = false;
    let sceneVisible = true;
    let initialized = false;
    let x = 0;
    let y = 0;
    let previousLensX = 0;
    let previousLensY = 0;
    let velocityX = 0;
    let velocityY = 0;
    let strength = 0;
    let eligibleHoverSeconds = 0;
    let bounds = null;
    let activeConfig = config;
    const inputIsEligible = () => activeConfig.enabled && finePointerQuery.matches && !reducedMotionQuery.matches;
    const onPointerMove = (event) => {
        if (event.pointerType !== "mouse")
            return;
        clientX = event.clientX;
        clientY = event.clientY;
        hasMouse = true;
    };
    const clearPointer = () => {
        hasMouse = false;
        eligibleHoverSeconds = 0;
    };
    const resetPointer = () => {
        hasMouse = false;
        initialized = false;
        velocityX = 0;
        velocityY = 0;
        strength = 0;
        eligibleHoverSeconds = 0;
    };
    const onPointerOut = (event) => {
        if (event.relatedTarget === null)
            clearPointer();
    };
    const onVisibilityChange = () => {
        if (document.visibilityState !== "visible")
            resetPointer();
    };
    const invalidateBounds = () => {
        bounds = null;
    };
    window.addEventListener("pointermove", onPointerMove, { passive: true, capture: true });
    window.addEventListener("pointerout", onPointerOut, { passive: true, capture: true });
    window.addEventListener("blur", clearPointer);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return {
        update(frameSeconds) {
            const eligible = hasMouse && sceneVisible && inputIsEligible();
            const rect = eligible
                ? bounds ?? (bounds = canvas.getBoundingClientRect())
                : null;
            const inside = rect !== null &&
                rect.width > 0 &&
                rect.height > 0 &&
                clientX >= rect.left &&
                clientX <= rect.right &&
                clientY >= rect.top &&
                clientY <= rect.bottom;
            const targetX = inside && rect ? clientX - rect.left : x;
            const targetY = inside && rect ? rect.bottom - clientY : y;
            if (inside && !initialized) {
                x = targetX;
                y = targetY;
                previousLensX = x;
                previousLensY = y;
                initialized = true;
            }
            const safeSeconds = Math.max(frameSeconds, 1 / 240);
            const positionAlpha = smoothingAlpha(frameSeconds, activeConfig.pointerSmoothingMs);
            x += (targetX - x) * positionAlpha;
            y += (targetY - y) * positionAlpha;
            const followerVelocityX = (x - previousLensX) / safeSeconds;
            const followerVelocityY = (y - previousLensY) / safeSeconds;
            previousLensX = x;
            previousLensY = y;
            const followerMagnitude = Math.hypot(followerVelocityX, followerVelocityY);
            const velocityScale = followerMagnitude > 0
                ? Math.min(followerMagnitude / 900, 1) / followerMagnitude
                : 0;
            const targetVelocityX = followerVelocityX * velocityScale;
            const targetVelocityY = followerVelocityY * velocityScale;
            const velocityAlpha = smoothingAlpha(frameSeconds, activeConfig.pointerSmoothingMs * 1.3);
            velocityX += (targetVelocityX - velocityX) * velocityAlpha;
            velocityY += (targetVelocityY - velocityY) * velocityAlpha;
            eligibleHoverSeconds = inside ? eligibleHoverSeconds + frameSeconds : 0;
            const delayElapsed = eligibleHoverSeconds * 1000 >= activeConfig.hoverDelayMs;
            const targetStrength = inside && delayElapsed ? 1 : 0;
            const duration = targetStrength > strength ? activeConfig.attackMs : activeConfig.releaseMs;
            strength +=
                (targetStrength - strength) * smoothingAlpha(frameSeconds, duration);
            strength = clamp(strength, 0, 1);
            if (strength < 0.0001 && !inside) {
                strength = 0;
                velocityX = 0;
                velocityY = 0;
                initialized = false;
            }
            return { x, y, velocityX, velocityY, strength };
        },
        setConfig(nextConfig) {
            activeConfig = nextConfig;
            resetPointer();
        },
        setSceneVisible(visible) {
            sceneVisible = visible;
            if (!visible)
                resetPointer();
        },
        invalidateBounds,
        destroy() {
            window.removeEventListener("pointermove", onPointerMove, true);
            window.removeEventListener("pointerout", onPointerOut, true);
            window.removeEventListener("blur", clearPointer);
            document.removeEventListener("visibilitychange", onVisibilityChange);
        },
    };
}
//# sourceMappingURL=pointer-field.js.map